import type { IClientResponse } from "@gen/app-query";
import { CoreQuery } from "@gen/app-query";
import { type AnyRoute, createRoute } from "@tanstack/react-router";

import { ClientFees } from "./ClientFees";

const CLIENT_FEES_DEFAULT_TITLE = "Client Custom Fees";

export function createClientFeesRoute<P extends AnyRoute>(parent: P) {
  return createRoute({
    getParentRoute: () => parent,
    path: "admin/clients/$clientId/fees",
    component: ClientFees,
    loader: ({ params, context }) => {
      const queryClient = context.queryClient;
      const queryKey = CoreQuery.admin_getClientQueryKey(params.clientId);

      // `context` is untyped here because the route factory takes an AnyRoute
      // parent, which erases the router's AuthCtx context; cast the result.
      const cachedClient = queryClient.getQueryData(queryKey) as
        | IClientResponse
        | undefined;

      const calculateTitle = (prefix: string, name: string) => {
        return prefix + ": " + name;
      };

      let titlePromise = queryClient
        .fetchQuery({
          queryKey,
          queryFn: () => CoreQuery.Client().admin_getClient(params.clientId),
        })
        .then((r: IClientResponse) =>
          calculateTitle(CLIENT_FEES_DEFAULT_TITLE, r.name),
        );

      if (cachedClient) {
        titlePromise = Promise.resolve(
          calculateTitle(CLIENT_FEES_DEFAULT_TITLE, cachedClient.name),
        );
      } else {
        queryClient.prefetchQuery({
          queryKey,
          queryFn: () => CoreQuery.Client().admin_getClient(params.clientId),
        });
      }

      return {
        titlePromise,
      };
    },
    head: () => ({
      meta: [
        {
          title: CLIENT_FEES_DEFAULT_TITLE,
        },
      ],
    }),
  });
}
